import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { profileAccountService } from "../../../store/actions";
import EditTagModal from "./EditTransactionPrivateTagModal";
import DeleteModal from "./DeleteTxTag";

interface TransactionNote {
  tag_name: string;
  transaction_hash: string;
  id: string;
}

interface ApiResponse {
  transactionTags: TransactionNote[];
}

interface AccountData {
  message: string;
  signature: string;
}

const TransactionPrivateTags: React.FC = () => {
  const [notes, setNotes] = useState<TransactionNote[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TransactionNote | null>(null);
  const account = useSelector((state: any) => state?.common?.account);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTag, setDeleteTag] = useState<TransactionNote | null>(null);

  const fetchNotes = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const data: AccountData = {
        message: account?.message,
        signature: account?.signature,
      };

      const response: ApiResponse =
        await profileAccountService.getTxPrivateNotes(data);
      setNotes(response.transactionTags || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching transaction notes:", err);
      setError("Failed to load private notes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  const handleEdit = (item: TransactionNote): void => {
    if (!item.transaction_hash) {
      console.error("Cannot edit tag without Transaction Hash :", item);
      return;
    }
    setSelectedTag(item);
    setIsEditModalOpen(true);
  };

  const handleDelete = (note: TransactionNote): void => {
    if (!note.transaction_hash) {
      console.error("Cannot edit tag without Transaction Hash :", note);
      return;
    }
    setDeleteTag(note);
    setIsDeleteModalOpen(true);
  };

  // Truncate long transaction hashes
  const truncateHash = (hash: string) => {
    if (!hash) return "";
    return `${hash.substring(0, 6)}...${hash.substring(hash.length - 4)}`;
  };

  if (isLoading) {
    return (
      <tr>
        <td colSpan={3} className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan={3} className="text-center py-4 text-danger">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
        </td>
      </tr>
    );
  }

  if (!notes.length) {
    return (
      <tr className="border-bottom-0">
        <td colSpan={3}>
          <div className="text-center py-24">
            <div className="mb-4 mt-4">
              <span
                className="bg-warning bg-opacity-10 border border-warning border-opacity-25 text-yellow-600 fs-70x content-center rounded-pill mx-auto"
                style={{ width: "4rem", height: "4rem" }}
              >
                <i className="bi bi-inboxes fs-4"></i>
              </span>
            </div>
            <h3 className="h5">There are no private notes found</h3>
            <p className="text-muted mb-0">Please try again later</p>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      {notes.map((note, index) => (
        <tr key={`${note.transaction_hash}-${index}`} className="bs-table-row">
          <td className="bs-table-cell">
            <a
              href={`/tx/${note.transaction_hash}`}
              className="text-primary"
              title={note.transaction_hash}
            >
              {truncateHash(note.transaction_hash)}
            </a>
          </td>
          <td className="bs-table-cell">
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: "300px" }}
            >
              {note.tag_name}
            </span>
          </td>
          <td className="bs-table-cell">
            <div className="d-flex">
              <button
                className="btn btn-sm btn-ghost-secondary me-2"
                title="Edit"
                onClick={() => handleEdit(note)}
              >
                <i className="bi bi-pencil"></i>
              </button>
              <button
                className="btn btn-sm btn-ghost-danger"
                title="Delete"
                onClick={() => handleDelete(note)}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          </td>
        </tr>
      ))}

      {selectedTag && (
        <EditTagModal
          show={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedTag(null);
          }}
          tagItem={selectedTag}
          onSuccess={fetchNotes}
        />
      )}

      {deleteTag && (
        <DeleteModal
          show={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setDeleteTag(null);
          }}
          tagItem={deleteTag}
          onSuccess={fetchNotes}
        />
      )}
    </>
  );
};

export default TransactionPrivateTags;
